/**
 * Live incentive preview shown inside the New/Edit Order dialog.
 *
 * As the operator picks a customer + items, we debounce-call
 * `POST /incentives/preview` so the person creating the order sees exactly
 * what they'll earn if their admin's rule fires — no waiting for a payment.
 *
 * Renders nothing when the caller isn't scoped to a matching rule.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy } from "@phosphor-icons/react";

export default function IncentiveHint({ customerId, items, ownerId, testId = "incentive-hint" }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  // Serialise the priced-line shape into a stable string so the effect only
  // re-runs when a value the pricing rule actually cares about changes.
  const itemsKey = JSON.stringify(items?.map((i) => [i.product_id, i.qty, i.price, i.gst]) || []);
  useEffect(() => {
    if (!items?.length || !customerId) { setPreview(null); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/incentives/preview", {
          customer_id: customerId,
          items: items.map((it) => ({
            product_id: it.product_id, qty: +it.qty || 0,
            price: +it.price || 0, gst: +it.gst || 0,
          })),
          owner_id: ownerId || undefined,
        });
        if (!cancelled) setPreview(r.data);
      } catch {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, ownerId, itemsKey]);

  if (!items?.length) return null;
  if (loading && !preview) {
    return <div className="text-[11px] text-muted-foreground text-right" data-testid={`${testId}-loading`}>Calculating incentive…</div>;
  }
  if (!preview?.matched) {
    return (
      <div className="text-[11px] text-muted-foreground text-right" data-testid={`${testId}-none`}>
        No matching incentive rule for this order.
      </div>
    );
  }
  const rate = preview.rate_pct;
  const amt = (preview.expected_amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  return (
    <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/5 px-3 py-1.5 text-xs text-emerald-800 dark:text-emerald-200 ml-auto"
         data-testid={testId}>
      <Trophy size={13} weight="duotone" />
      <span>
        Incentive:
        {rate != null ? <b className="mx-1">{rate}%</b> : preview.structure === "fixed" ? <b className="mx-1">flat ₹{preview.fixed_amount}</b> : <span className="mx-1">{preview.structure}</span>}
        · <b>₹{amt}</b>
      </span>
      {preview.rule_name && <span className="text-muted-foreground">· {preview.rule_name}</span>}
    </div>
  );
}
